import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type getServiceAgreementParams,
  useGetServiceAgreementsWithProvider,
} from '../../pages/Settings/Dealers/dealers.queries';
import {
  getServiceAgreementStatusPriority,
  mapServiceAgreementBody,
} from '../../utils/serviceAgreement';
import type { ServiceAgreement } from '../../components/ServiceAgreement/Common/ServiceAgreementTypes';
import { PaginationConfig } from '../../components/ViewContainer';

const DEFAULT_PAGE = '1';
const DEFAULT_LIMIT: PaginationConfig['limit'] = '10';

export type ServiceAgreementListData = {
  visibleItems: ServiceAgreement[];
  totalItems: number;
  pagination: PaginationConfig;
  showAddCta: boolean;
  visibleItemsText: string | undefined;
  isFetching: boolean;
  isLoading: boolean;
  refresh: () => void;
};

const getAgreementName = (agreement: ServiceAgreement): string => agreement.provider?.name || '';

/**
 * Dealer-side Service Agreements list (Settings › Dealers). The customer
 * perspective was retired with the aggregated Customers list (ticket 05,
 * CSSD-1649): Customers/Invites/Requests now have their own server-paged
 * queries, so this hook only ever serves `ServiceAgreementsList` for dealers.
 */
export default function useServiceAgreementsList(): ServiceAgreementListData {
  const { t } = useTranslation();

  const [page, setPage] = useState<string>(DEFAULT_PAGE);
  const [limit, setLimit] = useState<PaginationConfig['limit']>(DEFAULT_LIMIT);

  const effectiveLimit = limit ?? DEFAULT_LIMIT;

  const queryFilters = useMemo(() => ({ page, limit: effectiveLimit }), [page, effectiveLimit]);

  const dealersQuery = useGetServiceAgreementsWithProvider(
    queryFilters as getServiceAgreementParams
  );

  const { isFetching, isLoading, refetch, data: dealersData } = dealersQuery;

  const rawItems = useMemo(() => dealersData?.items ?? [], [dealersData]);

  const filteredItems = useMemo(() => {
    return rawItems.map(mapServiceAgreementBody).sort((a, b) => {
      const pa = getServiceAgreementStatusPriority(a.computedServiceAgreementStatus);
      const pb = getServiceAgreementStatusPriority(b.computedServiceAgreementStatus);
      if (pa !== pb) return pa - pb;
      return getAgreementName(a).localeCompare(getAgreementName(b));
    });
  }, [rawItems]);

  const totalItems = dealersData?.total ?? 0;

  const totalPages = Math.max(1, Math.ceil(totalItems / Number(effectiveLimit)));
  const pages = totalPages.toString();
  // Clamp the requested page to the available range so a shrinking result set
  // (e.g. a background refetch returning fewer items) can't strand the view on
  // an out-of-range page and render an empty state while earlier pages have items.
  const currentPage = Math.min(Math.max(1, Number(page)), totalPages);

  const visibleItems = filteredItems;

  const handleLimitChange = useCallback((nextLimit: PaginationConfig['limit']) => {
    if (!nextLimit) return;
    setLimit(nextLimit);
    setPage(DEFAULT_PAGE);
  }, []);

  const pagination: PaginationConfig = useMemo(
    () => ({
      enabled: totalItems > Number(effectiveLimit),
      pages,
      page: currentPage.toString(),
      limit: effectiveLimit,
      onPageChange: (next: string) => setPage(next),
      onLimitChange: handleLimitChange,
    }),
    [totalItems, effectiveLimit, pages, currentPage, handleLimitChange]
  );

  const showAddCta = totalItems === 0;

  const visibleItemsText = useMemo(
    () =>
      isFetching
        ? undefined
        : t('numItemsDisplayed', {
            count: visibleItems.length,
            total: totalItems,
          }),
    [totalItems, isFetching, visibleItems.length, t]
  );

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Review opens in a new tab (`navigateToReviewPage`). Global RQ defaults disable
  // refetchOnWindowFocus, so the list tab stays stale after accept/decline unless
  // we explicitly refetch when the user returns to this tab.
  useEffect(() => {
    const refetchWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', refetchWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', refetchWhenVisible);
    };
  }, [refetch]);

  useEffect(() => {
    setPage(DEFAULT_PAGE);
  }, [effectiveLimit]);

  return {
    visibleItems,
    totalItems,
    pagination,
    showAddCta,
    visibleItemsText,
    isFetching,
    isLoading,
    refresh,
  };
}
